// Mantener sincronizado con backend/utils/permissions.js
export const PERM_KEYS = {
  PRODUCTS_ACCESS: 'products:access',
  SALES_ACCESS: 'sales:access',
  SALES_HISTORY: 'sales:history',
  CUSTOMERS_ACCESS: 'customers:access',
  SUPPLIERS_ACCESS: 'suppliers:access',
  PURCHASES_ACCESS: 'purchases:access',
  CASH_ACCESS: 'cash:access',
  REPORTS_ACCESS: 'reports:access',
  REPORTS_IVA: 'reports:iva',
  FISCAL_ACCESS: 'fiscal:access',
  USERS_MANAGE: 'users:manage',
  INVOICE_ACCESS: 'invoice:access',
  INVOICE_REPRINT: 'invoice:reprint',
  INVOICE_VOID: 'invoice:void',
  SETTINGS_BUSINESS: 'settings:business',
  SETTINGS_PRINT: 'settings:print',
  QUOTATIONS_ACCESS: 'quotations:access',
  AUDIT_ACCESS: 'audit:access',
  SETTINGS_ACCESS: 'settings:access',
  STOCK_MOVEMENTS: 'stock:movements',
  SALES_WITHOUT_CASH: 'sales:without_cash',
};

const ALL_TRUE = Object.values(PERM_KEYS).reduce((acc, k) => { acc[k] = true; return acc; }, {});

const DEFAULTS = {
  owner: { ...ALL_TRUE },
  admin: { ...ALL_TRUE },
  cashier: {
    [PERM_KEYS.SALES_ACCESS]: true,
    [PERM_KEYS.SALES_HISTORY]: true,
    [PERM_KEYS.INVOICE_ACCESS]: true,
    [PERM_KEYS.INVOICE_REPRINT]: true,
    [PERM_KEYS.INVOICE_VOID]: false,
    [PERM_KEYS.QUOTATIONS_ACCESS]: true,
  },
  seller: {
    [PERM_KEYS.SALES_ACCESS]: true,
    [PERM_KEYS.QUOTATIONS_ACCESS]: true,
  },
  accounting: {
    [PERM_KEYS.REPORTS_ACCESS]: true,
    [PERM_KEYS.REPORTS_IVA]: true,
    [PERM_KEYS.FISCAL_ACCESS]: true,
    [PERM_KEYS.INVOICE_ACCESS]: true,
    [PERM_KEYS.INVOICE_REPRINT]: true,
  },
  warehouse: {
    [PERM_KEYS.PRODUCTS_ACCESS]: true,
    [PERM_KEYS.STOCK_MOVEMENTS]: true,
  },
  purchasing: {
    [PERM_KEYS.SUPPLIERS_ACCESS]: true,
    [PERM_KEYS.PURCHASES_ACCESS]: true,
    [PERM_KEYS.PRODUCTS_ACCESS]: true,
  },
};

function readPerm(perms, key) {
  if (!perms) return undefined;
  if (typeof perms.get === 'function') return perms.get(key);
  return perms[key];
}

export function hasPerm(user, perm) {
  if (!user) return false;

  // Bloqueo estricto de funcionalidades POS para usuarios Ecommerce Lite
  const serviceType = user.tenantId?.serviceType || 'full_pos';
  if (serviceType === 'ecommerce_lite') {
    const BLOCKED_FOR_LITE = [
      PERM_KEYS.SALES_ACCESS,      // Punto de Venta
      PERM_KEYS.CASH_ACCESS,       // Apertura/Cierre de Caja
      PERM_KEYS.REPORTS_IVA,       // Reportes fiscales tradicionales
      PERM_KEYS.SETTINGS_PRINT,    // Configuración de impresoras
      PERM_KEYS.SUPPLIERS_ACCESS,  // Proveedores
      PERM_KEYS.PURCHASES_ACCESS,  // Compras
      PERM_KEYS.QUOTATIONS_ACCESS, // Presupuestos
    ];
    if (BLOCKED_FOR_LITE.includes(perm)) return false;
  }

  // NOTE: Previously we forced fiscal UI access here for testing/impersonation.
  // Backend is now authoritative and admin controllers were neutralized, so
  // do not bypass permission checks on the client side.
  const v = readPerm(user.permissions, perm);
  if (v === true) return true;
  if (v === false) return false;
  const defaults = DEFAULTS[user.role] || {};
  return !!defaults[perm];
}

export function effectivePerms(user) {
  const role = user?.role || 'cashier';
  const base = { ...(DEFAULTS[role] || {}) };
  const src = user?.permissions || {};
  const entries = typeof src.entries === 'function' ? Array.from(src.entries()) : Object.entries(src);
  const out = { ...base };
  for (const [k, v] of entries) {
    if (typeof v === 'boolean') out[k] = v;
  }
  return out;
}
